/**
 * jobPages — load a GET /jobs list one page at a time.
 *
 * Lambda refuses a response over 6 MB, and GET /jobs outgrew it: on prod 2026-09-28 the
 * unfiltered list was 22 MB, Bad Idea Co. (the Invoices job picker) 20 MB and status=Completed
 * 5.9 MB, so those calls failed with a 5xx. The API now pages on request:
 *
 *   GET /jobs?<filters>&page_size=N[&cursor=C]  ->  { jobs: [...], next_cursor: C2 | null }
 *
 * Pages are keyed on job_id (newest job first, each page below the cursor), which never changes:
 * a job edited while the pages load (priority, due date, status) can only move itself, never
 * push a neighbour out of the list. next_cursor null means the last page. A page can hold fewer
 * than page_size rows (the API also caps each page's bytes), so the loop follows next_cursor and
 * never stops on a short page. `summary=true` (a filter like any other) gives the same rows with
 * tasks and parts empty, for screens that read only the job fields.
 *
 * fetchAllJobPages(getPage) calls getPage({ page_size[, cursor] }) until next_cursor is null, then
 * sorts the rows into the order GET /jobs has always used (priority, due date, newest job first):
 * the list the one-shot call used to return. A job_id seen twice keeps its first copy. An API
 * that predates paging ignores page_size and answers the plain list, which is the answer.
 * isCancelled() is checked after every page; when it says true the result is null (a newer load
 * replaced this one).
 */

export const JOBS_PAGE_SIZE = 1000; // the API's maximum; its byte cap keeps each page under the limit
const MAX_PAGES = 500;

// GET /jobs's ORDER BY: priority_level ASC, due_date ASC, job_id DESC; PostgreSQL puts NULLs
// last in ascending order. due_date is one ISO format ("YYYY-MM-DDT00:00:00Z"), so strings compare.
export function compareJobsLikeServer(a, b) {
  const pa = a.priority_level, pb = b.priority_level;
  if (pa !== pb) {
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa - pb;
  }
  const da = a.due_date, db = b.due_date;
  if (da !== db) {
    if (da == null) return 1;
    if (db == null) return -1;
    return da < db ? -1 : 1;
  }
  return b.job_id - a.job_id;
}

export async function fetchAllJobPages(getPage, { pageSize = JOBS_PAGE_SIZE, isCancelled } = {}) {
  const rows = [];
  const seen = new Set();
  let cursor = null;
  for (let n = 0; n < MAX_PAGES; n += 1) {
    const page = await getPage(cursor === null ? { page_size: pageSize } : { page_size: pageSize, cursor });
    if (isCancelled && isCancelled()) return null;
    if (n === 0 && Array.isArray(page)) return page;
    if (!page || !Array.isArray(page.jobs)) {
      throw new Error("GET /jobs: unexpected page response");
    }
    for (const job of page.jobs) {
      if (!seen.has(job.job_id)) {
        seen.add(job.job_id);
        rows.push(job);
      }
    }
    const next = page.next_cursor;
    if (next === null || next === undefined) return rows.sort(compareJobsLikeServer);
    if (!Number.isInteger(next) || (cursor !== null && next >= cursor)) {
      throw new Error(`GET /jobs: paging did not advance (cursor ${cursor} -> ${next})`);
    }
    cursor = next;
  }
  throw new Error(`GET /jobs: more than ${MAX_PAGES} pages`);
}

/** fetch() one page: `query` is the caller's filters (URLSearchParams or a query string). */
export async function fetchJobsPage(apiBase, query, page) {
  const params = new URLSearchParams(query);
  params.set("page_size", String(page.page_size));
  if (page.cursor !== undefined) params.set("cursor", String(page.cursor));
  const res = await fetch(`${apiBase}/jobs?${params.toString()}`);
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  return res.json();
}
